import { Icon } from '@mui/material';
import { message } from 'antd';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { postBulkAssignStudent } from '../../shared/api/assignationRequest';
import { getSerieInfoV2 } from '../../shared/api/serieRequest';
import DefaultCoverV2 from '../../shared/cover/DefaultCoverV2';
import { Serie } from '../../shared/types/Serie';

import { useAuth } from '../../shared/context/TokenContext';
import EpisodeRow, { EpisodeStatus } from '../components/EpisodeRow';
import ModalAssignements from '../components/ModalAssignements';

import { useLocation, useNavigate } from 'react-router-dom';
import { Episode } from '../../shared/types/Episode';
import { mixpanelInstance } from '../../shared/utils/mixpanel';
import ModalEpisodeEdifice from '../components/ModalEpisodeEdifice';
import '../../shared/css/ui.css';
import '../css/PanelSerie.css';

interface PanelSerieProps {
  serie: Serie;
  close: () => void;
}

const KIT_PEDAGO = [
  { id: 210, link: 'https://library.edifice.io/document/a7985d50-b4a2-4623-894f-119567f9d061' },
  { id: 66, link: 'https://library.edifice.io/document/dc248833-ecb7-4366-b457-e37f1e7be261' },
  { id: 135, link: 'https://library.edifice.io/document/c923a02f-4638-42e0-ab60-913ac79befd0' },
  { id: 298, link: 'https://library.edifice.io/document/81b0e130-ada2-4baf-82e7-7f8edc438fad' },
  { id: 74, link: 'https://library.edifice.io/document/8d96a98b-30b4-4a4f-860e-f790a8e8c652' },
  { id: 246, link: 'https://library.edifice.io/document/89cb0773-5a2d-4a79-8fed-31089455eb47' },
  { id: 118, link: 'https://library.edifice.io/document/a78a6c4a-a2c6-4e02-a27c-dc05cdcfff8b' },
];

const TEMPS_FORT = [
  {
    id: 118,
    endAt: '06/07/2026',
    name: 'Temps fort Dessins animés - 06/07/2026',
  },
];

const getCoverUrl = (serie: Serie) => `https://newbrand.s3.eu-west-3.amazonaws.com/series/${serie.uid}/couvertures/${serie.img}`;

const PanelSerieSkeleton = () => (
  <div className="ps-skeleton" aria-busy="true" aria-label="Chargement de la série">
    <div className="ps-skeleton__hero">
      <div className="nbk-skeleton ps-skeleton__cover" />
      <div className="ps-skeleton__lines">
        <div className="nbk-skeleton" style={{ width: '60%', height: 32 }} />
        <div className="nbk-skeleton" style={{ width: '40%', height: 20 }} />
        <div className="nbk-skeleton" style={{ width: '100%', height: 16 }} />
        <div className="nbk-skeleton" style={{ width: '90%', height: 16 }} />
        <div className="nbk-skeleton" style={{ width: 220, height: 48 }} />
      </div>
    </div>
    {[0, 1, 2].map(i => (
      <div key={i} className="nbk-skeleton ps-skeleton__episode" />
    ))}
  </div>
);

const PanelSerie: FunctionComponent<PanelSerieProps> = (props: PanelSerieProps) => {
  const [messageApi, contextHolder] = message.useMessage();
  const { idClasse, isEdifice, isTeacher } = useAuth();
  const navigation = useNavigate();
  const location = useLocation();

  const [details, setDetails] = useState<Serie | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);
  const [indexSeason, setIndexSeason] = useState(0);
  const [assignEp, setAssignEp] = useState<Episode | null>(null);
  const [assignedEpisodes, setAssignedEpisodes] = useState<number | null>(null);
  const [statuses, setStatuses] = useState<Record<number, EpisodeStatus>>({});

  const [infoEp, setInfoEp] = useState<any>(null);
  const [openModalEdifice, setOpenModalEdifice] = useState(false);

  const loadDetails = useCallback(async () => {
    setLoadError(false);
    const res = await getSerieInfoV2(`${props.serie.id}`);
    if (res?.serie) setDetails(res.serie);
    else setLoadError(true);
  }, [props.serie.id]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const openAssignModal = (episode: Episode) => {
    setAssignedEpisodes(null);
    setAssignEp(episode);
  };

  const success = () => {
    messageApi.open({
      type: 'success',
      content: "L'assignation a été effectuée avec succès",
    });
  };

  const error = () => {
    messageApi.open({
      type: 'error',
      content: "Il y a eu un problème lors de l'assignation. Veuillez réessayer",
    });
  };

  const openEpisode = (episode: Episode, idSerie: number) => {
    navigation(`/reader/${episode.id}/${episode.origStartingNode ? episode.origStartingNode : episode.startingNode}`, {
      state: {
        idSerie: idSerie,
        episode: episode,
        params: { isDys: false, fontSize: 1 },
        // The reader brings the user back to this page (home, collection...) with the serie reopened
        returnTo: location.pathname,
      },
    });
  };

  const onClickEpisode = (episode: Episode, idSerie: number) => {
    setInfoEp({ episode, idSerie });
    if (episode.statut === 'free') openEpisode(episode, idSerie);
    else if (isEdifice && !isEdifice()) openEpisode(episode, idSerie);
    else if (isEdifice && isEdifice()) {
      if (isTeacher && !isTeacher()) setOpenModalEdifice(true);
      else {
        mixpanelInstance.track('ED - OT - Open EP');
        openEpisode(episode, idSerie);
      }
    }
  };

  const onStatusLoaded = useCallback((episode: Episode, status: EpisodeStatus) => {
    setStatuses(prev => (prev[episode.id] === status ? prev : { ...prev, [episode.id]: status }));
  }, []);

  const serie = details || props.serie;
  const seasons = details?.Seasons ?? [];
  const season = seasons[indexSeason];
  const episodes = season?.Episodes ?? [];
  const tempsFort = TEMPS_FORT.find(item => item.id === details?.id);
  const kitPedago = KIT_PEDAGO.find(item => item.id === details?.id);
  const isTeacherView = !!(isEdifice && isEdifice() && isTeacher && isTeacher());
  const episodeCount = seasons.reduce((total, s) => total + (s.Episodes?.length ?? 0), 0);

  // Main call to action: resume the episode in progress, else the first one not read yet
  const getMainAction = () => {
    if (episodes.length === 0) return null;
    const isReady = episodes.every(e => statuses[e.id]);
    if (!isReady) return { isReady, label: 'Commencer la lecture', episode: episodes[0] };

    const inProgress = episodes.find(e => statuses[e.id] === 'pause');
    if (inProgress) return { isReady, label: `Reprendre l'épisode ${inProgress.number}`, episode: inProgress };

    const next = episodes.find(e => statuses[e.id] !== 'finished');
    if (!next) return { isReady, label: "Relire depuis l'épisode 1", episode: episodes[0] };
    if (next === episodes[0]) return { isReady, label: 'Commencer la lecture', episode: next };
    return { isReady, label: `Lire l'épisode ${next.number}`, episode: next };
  };

  const mainAction = getMainAction();

  return (
    <div className="ps">
      {contextHolder}

      <div className="ps-topbar">
        <span className="ps-topbar__handle" aria-hidden="true" />
        <button type="button" className="ps-topbar__close" onClick={props.close} aria-label="Fermer la fiche de la série">
          <Icon>close</Icon>
        </button>
      </div>

      {loadError ? (
        <div className="ps-error" role="alert">
          <Icon aria-hidden="true">cloud_off</Icon>
          <h2>Impossible de charger cette série</h2>
          <p>Vérifiez votre connexion internet puis réessayez.</p>
          <button type="button" className="nbk-btn nbk-btn--primary" onClick={loadDetails}>
            <Icon aria-hidden="true">refresh</Icon>
            Réessayer
          </button>
        </div>
      ) : !details ? (
        <PanelSerieSkeleton />
      ) : (
        <>
          <header className="ps-hero">
            <div className="ps-hero__backdrop" style={{ backgroundImage: `url(${getCoverUrl(serie)})` }} aria-hidden="true" />
            <div className="ps-hero__content">
              <div className="ps-hero__cover">
                <DefaultCoverV2 serie={serie} style={{ minWidth: '100%', maxWidth: '100%', margin: 0, borderRadius: 14 }} />
              </div>

              <div className="ps-hero__info">
                {tempsFort ? (
                  <span className="ps-badge">
                    <Icon aria-hidden="true">bolt</Icon>
                    {tempsFort.name}
                  </span>
                ) : null}
                <h1 className="ps-hero__title">{details.name}</h1>

                <ul className="ps-meta" aria-label="Informations sur la série">
                  {details.age ? <li>{details.age} ans</li> : null}
                  <li>{details.lang === 'fr' ? '🇫🇷 Français' : '🇫🇷🇬🇧 Bilingue'}</li>
                  {props.serie.mainGenre ? <li>{props.serie.mainGenre}</li> : null}
                  {seasons.length > 1 ? <li>{seasons.length} saisons</li> : null}
                  {episodeCount > 0 ? <li>{episodeCount} épisodes</li> : null}
                </ul>

                {details.author || details.illustrator ? (
                  <p className="ps-credits">
                    {details.author ? (
                      <span>
                        Écrit par <strong>{details.author}</strong>
                      </span>
                    ) : null}
                    {details.illustrator ? (
                      <span>
                        Illustré par <strong>{details.illustrator}</strong>
                      </span>
                    ) : null}
                  </p>
                ) : null}

                {season?.summary ? <p className="ps-summary">{season.summary}</p> : null}

                <div className="ps-actions">
                  {mainAction ? (
                    <button
                      type="button"
                      className="nbk-btn nbk-btn--primary ps-actions__main"
                      onClick={() => onClickEpisode(mainAction.episode, details.id)}>
                      <Icon aria-hidden="true">{mainAction.isReady ? 'play_arrow' : 'menu_book'}</Icon>
                      {mainAction.label}
                    </button>
                  ) : null}
                  {kitPedago && isTeacherView ? (
                    <a className="nbk-btn nbk-btn--ghost" href={kitPedago.link} target="_blank" rel="noreferrer">
                      <Icon aria-hidden="true">school</Icon>
                      Kit pédagogique
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </header>

          <section className="ps-episodes" aria-labelledby="ps-episodes-title">
            <div className="ps-episodes__header">
              <h2 id="ps-episodes-title" className="ps-episodes__title">
                Épisodes
              </h2>
              {seasons.length > 1 ? (
                <div className="ps-seasons" role="tablist" aria-label="Saisons">
                  {seasons.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      role="tab"
                      aria-selected={indexSeason === i}
                      className={`ps-seasons__tab${indexSeason === i ? ' ps-seasons__tab--active' : ''}`}
                      onClick={() => {
                        setIndexSeason(i);
                        setAssignedEpisodes(null);
                      }}>
                      Saison {i + 1}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {episodes.length > 0 ? (
              <ol className="ps-episodes__list">
                {episodes.map(ep => (
                  <EpisodeRow
                    key={ep.id}
                    episode={ep}
                    serie={details}
                    seasonNumber={indexSeason}
                    assign={openAssignModal}
                    isAssigned={assignedEpisodes === ep.id}
                    onClick={onClickEpisode}
                    onStatusLoaded={onStatusLoaded}
                  />
                ))}
              </ol>
            ) : (
              <p className="ps-episodes__empty">Les épisodes de cette saison arrivent bientôt.</p>
            )}
          </section>
        </>
      )}

      {/* Modals are portals: keep their key events (Escape...) from also closing the sheet around the panel */}
      <div onKeyDown={e => e.stopPropagation()}>
        <ModalAssignements
          open={assignEp !== null}
          episode={assignEp}
          assign={async (students: any) => {
            if (idClasse) {
              const status = await postBulkAssignStudent(assignEp!.id, idClasse, students);

              if (status && status === 200) {
                success();
                setAssignedEpisodes(assignEp!.id);
                setAssignEp(null);
              } else error();
            } else error();
          }}
          close={() => setAssignEp(null)}
        />
        <ModalEpisodeEdifice
          episode={infoEp?.episode}
          idSerie={infoEp?.idSerie}
          open={openModalEdifice && infoEp && infoEp.episode}
          onCancel={() => setOpenModalEdifice(false)}
        />
      </div>
    </div>
  );
};

export default PanelSerie;
